import { codeHint, CountryCode, setSession, trpc, useCodeChannel, usePhone } from '@tg/app-core';
import { phoneView } from '@tg/domain';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initials } from '../lib/data';
import { clearCache } from '../lib/query';
import { lsGet, lsSet, useLocal } from '../lib/storage';
import { I } from '../ui/icons';
import { toast } from '../ui/overlay';
import { copyText, inTelegram, Mock, openInBrowser, osOf } from '../ui/FirstSteps';
import { Head, Top } from '../ui/parts';

/** Повтор кода — через полторы минуты (как RESEND_SEC на сервере: каскад Telegram → звонок → SMS) */
const RESEND = 90;

/** Сохранённый номер (E.164); раньше хранились 10 цифр без +7 */
const savedPhone = (v: string) => (v && v[0] !== '+' ? '+7' + v : v);

const errText = (e: unknown) => (e instanceof Error && e.message && !/fetch|network/i.test(e.message) ? e.message : 'Нет связи с сервером, попробуйте ещё раз');

/* R1. Вход по номеру — единый для новых и существующих.
   Три вида: сам; по ссылке тренера (?invite=CODE) — карточка тренера;
   тренер выпустил новую ссылку — по прежней красная плашка, форма входа остаётся.
   У клиента один тренер (REG-5): ссылка другого тренера при входе ничего не меняет. */
export function Phone() {
  const nav = useNavigate();
  const [code] = useLocal<string | undefined>('invite', undefined);
  const inv = trpc.auth.invite.useQuery({ code: code || '' }, { enabled: !!code, retry: 0 });
  /* Ссылка открыта в Telegram — сначала просим перейти в браузер (R0); «Продолжить в Telegram» не запрещаем */
  const [stay, setStay] = useLocal('tgStay', false);
  if (inTelegram() && !stay) return <TgOpen code={code} inv={inv.data} onStay={() => setStay(true)} />;
  return <PhoneForm inv={inv.data} nav={nav} />;
}

type Inv = { status: string; trainer?: { name: string; photo?: string | null; city?: string | null } | null } | undefined;

/* R0. Ссылка открыта во встроенном браузере Telegram: оттуда приложение не добавить на экран,
   а вход не сохранится при переходе в Safari или Chrome — поэтому в браузер до входа, чтобы входить один раз */
function TgOpen({ code, inv, onStay }: { code?: string; inv: Inv; onStay: () => void }) {
  const and = osOf() === 'android';
  const br = and ? 'Chrome' : 'Safari';
  const url = location.origin + (code ? '/j/' + code : '/');
  const tr = inv?.status === 'ok' ? inv.trainer : null;
  return (
    <div className="pg auth tgo">
      {tr ? (
        <div className="invc">
          <div className="av">{tr.photo ? <img src={tr.photo} alt="" /> : initials(...(tr.name.split(' ') as [string, string]))}</div>
          <div>
            <em>Приглашение тренера</em>
            <b>{tr.name}</b>
            <s>приглашает вас тренироваться{tr.city ? ' · ' + tr.city : ''}</s>
          </div>
        </div>
      ) : null}
      <Head title={'Откройте в ' + br} sub={'Из Telegram приложение не поставить на экран телефона. Перейдите в ' + br + ' и войдите там.'} />
      <button className="btn pri" onClick={() => openInBrowser(url)}>
        <I n="link" />
        Открыть в {br}
      </button>
      <div className="card tgalt">
        <b>Не открылось?</b>
        <Mock k={and ? 'tg-and' : 'tg-ios'} cls="sm" />
        <p>
          Нажмите <em>{and ? '⋮' : '⋯'}</em> вверху справа → <em>«Открыть в {and ? 'браузере' : 'Safari'}»</em>. Или скопируйте ссылку в {br}.
        </p>
        <button className="btn sec" onClick={async () => toast((await copyText(url)) ? 'Ссылка скопирована' : url)}>
          Скопировать ссылку
        </button>
      </div>
      <button className="lnk tgstay" onClick={onStay}>
        Продолжить в Telegram
      </button>
    </div>
  );
}

function PhoneForm({ inv, nav }: { inv: Inv; nav: ReturnType<typeof useNavigate> }) {
  const [saved, setSaved] = useLocal('phone', '');
  const ph = usePhone(savedPhone(saved));
  const req = trpc.auth.requestCode.useMutation();

  const submit = async () => {
    if (!ph.ok || req.isPending) return;
    try {
      const r = await req.mutateAsync({ phone: ph.full, app: 'client' });
      setSaved(ph.full);
      lsSet('codeAt', Date.now() - (RESEND - r.resendIn) * 1000);
      nav('/code');
    } catch (e) {
      toast(errText(e));
    }
  };

  const st = inv?.status;
  const tr = inv?.trainer;
  const ini = tr ? initials(...(tr.name.split(' ') as [string, string])) : '';
  return (
    <div className="pg auth">
      {st === 'ok' && tr ? (
        <div className="invc">
          <div className="av">{tr.photo ? <img src={tr.photo} alt="" /> : ini}</div>
          <div>
            <em>Приглашение тренера</em>
            <b>{tr.name}</b>
            <s>приглашает вас тренироваться{tr.city ? ' · ' + tr.city : ''}</s>
          </div>
        </div>
      ) : st === 'expired' ? (
        <div className="invc exp" role="alert">
          <div className="av">
            <I n="link" />
          </div>
          <div>
            <b>Ссылка устарела</b>
            <s>Пожалуйста, запросите новую ссылку у тренера</s>
          </div>
        </div>
      ) : null}
      <Head title="Вход" sub="Введите номер телефона — пришлём код" />
      <form
        className="card fcard"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="lbl" htmlFor="tel">
          Номер телефона
        </label>
        <div className="fld" data-cc-box>
          <I n="phone" />
          <CountryCode className="pre" c={ph.c} onChange={ph.setCountry} />
          <input id="tel" type="tel" inputMode="tel" autoComplete="tel-national" placeholder={ph.placeholder} value={ph.value} onChange={(e) => ph.onInput(e.target.value)} />
        </div>
        <button className="btn pri" type="submit" disabled={!ph.ok || req.isPending}>
          Получить код
        </button>
        <p className="legal">Продолжая, вы соглашаетесь с условиями сервиса и обработкой персональных данных</p>
      </form>
    </div>
  );
}

/* R2. Код входа (Telegram → звонок → SMS): четыре ячейки, проверка сразу, повтор через полторы минуты.
   autocomplete="one-time-code" — код подставляется с клавиатуры (iOS, Android). */
export function Code() {
  const nav = useNavigate();
  const phone = savedPhone(lsGet('phone', ''));
  const [val, setVal] = useState('');
  const [left, setLeft] = useState(() => Math.max(0, RESEND - Math.round((Date.now() - lsGet('codeAt', 0)) / 1000)));
  const [sentAt, setSentAt] = useState(() => lsGet('codeAt', 0));
  const ch = useCodeChannel(phone, sentAt);
  const verify = trpc.auth.verify.useMutation();
  const req = trpc.auth.requestCode.useMutation();
  const inp = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!phone) nav('/phone', { replace: true });
  }, [phone, nav]);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);

  const check = async (code: string) => {
    try {
      const r = await verify.mutateAsync({ phone, code, app: 'client', invite: lsGet<string | undefined>('invite', undefined) });
      clearCache();
      setSession({ access: r.access, refresh: r.refresh, role: r.role, pid: r.pid });
      lsSet('invite', undefined);
      /* Номер новый — создание аккаунта, уже есть — главная */
      nav(r.isNew ? '/wiz/data' : '/', { replace: true });
    } catch (e) {
      toast(errText(e));
      setVal('');
      inp.current?.focus();
    }
  };
  const onInput = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 4);
    setVal(d);
    if (d.length === 4 && !verify.isPending) check(d);
  };
  const resend = async () => {
    try {
      const r = await req.mutateAsync({ phone, app: 'client' });
      lsSet('codeAt', Date.now() - (RESEND - r.resendIn) * 1000);
      setLeft(r.resendIn);
      setSentAt(Date.now());
    } catch (e) {
      toast(errText(e));
    }
  };

  return (
    <>
      <Top />
      <div className="pg auth">
        <Head title="Код для входа" sub={'Отправили на ' + phoneView(phone)} />
        <label className="otp" htmlFor="otp">
          <input ref={inp} id="otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={4} aria-label="Код для входа" autoFocus value={val} onChange={(e) => onInput(e.target.value)} />
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={i === val.length ? 'cur' : ''}>
              {val[i] || ''}
            </span>
          ))}
        </label>
        <p className={'code-ch' + (ch.channel ? ' ' + ch.channel : '')}>{codeHint(ch)}</p>
        <p className="resend">
          {left > 0 ? (
            `Отправить ещё раз через 0:${String(left).padStart(2, '0')}`
          ) : (
            <button className="lnk" onClick={resend}>
              Отправить код ещё раз
            </button>
          )}
        </p>
        <button className="lnk" onClick={() => nav('/phone', { replace: true })}>
          Изменить номер
        </button>
      </div>
    </>
  );
}
